import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { ArrowLeft, CalendarDays } from "lucide-react";
import { useSiteSection } from "@/hooks/useSiteContent";
import { siteImageUrl } from "@/hooks/useSiteData";
import { findPost, postParagraphs, postSlug } from "@/lib/blog";

export const Route = createFileRoute("/blog/$slug")({
  head: ({ params }) => {
    const title = params.slug.replace(/-/g, " ");
    return {
      meta: [
        { title: `${title} — Blog Contato` },
        { name: "description", content: `Leia "${title}" no blog da Contato Internet.` },
        { property: "og:title", content: `${title} — Blog Contato` },
        { property: "og:description", content: `Leia "${title}" no blog da Contato Internet.` },
        { property: "og:type", content: "article" },
        { name: "twitter:card", content: "summary_large_image" },
      ],
    };
  },
  component: PostPage,
  notFoundComponent: () => (
    <div className="mx-auto max-w-2xl px-5 py-24 text-center">
      <h1 className="text-2xl font-extrabold text-brand-deep">Post não encontrado</h1>
      <Link to="/blog" className="mt-4 inline-block font-extrabold text-primary hover:underline">
        Voltar para o blog
      </Link>
    </div>
  ),
  errorComponent: () => (
    <div className="mx-auto max-w-2xl px-5 py-24 text-center">
      <h1 className="text-2xl font-extrabold text-brand-deep">Não foi possível carregar o post</h1>
    </div>
  ),
});

function PostPage() {
  const { slug } = Route.useParams();
  const blog = useSiteSection("blog");
  const posts = (blog.items ?? []).filter((p) => p.title);
  const post = findPost(posts, slug);

  if (!post) throw notFound();

  const image = siteImageUrl(post.image);
  const paragraphs = postParagraphs(post);
  const others = posts.filter((p) => postSlug(p) !== slug).slice(0, 3);

  return (
    <>
      <section className="bg-[linear-gradient(110deg,var(--brand-sky-deep),var(--brand-deep))] py-12 text-brand-deep-foreground sm:py-16">
        <div className="mx-auto max-w-3xl px-5">
          <Link
            to="/blog"
            className="inline-flex items-center gap-1.5 text-xs font-extrabold uppercase tracking-[0.2em] text-brand-yellow"
          >
            <ArrowLeft className="size-4" /> Blog
          </Link>
          <h1 className="mt-3 text-2xl font-extrabold leading-tight sm:text-4xl">{post.title}</h1>
          {post.date ? (
            <p className="mt-3 inline-flex items-center gap-1.5 text-sm text-brand-sky">
              <CalendarDays className="size-4" />
              {post.date}
            </p>
          ) : null}
        </div>
      </section>

      <article className="bg-background py-10 sm:py-14">
        <div className="mx-auto max-w-3xl px-5">
          {image ? (
            <img
              src={image}
              alt={post.title}
              className="mb-8 aspect-[16/9] w-full rounded-3xl object-cover"
            />
          ) : null}

          <div className="space-y-4 text-base leading-relaxed text-foreground/90">
            {paragraphs.map((p, i) => (
              <p key={i}>{p}</p>
            ))}
          </div>

          {others.length ? (
            <div className="mt-14 border-t border-border pt-8">
              <h2 className="text-lg font-extrabold uppercase text-brand-deep">Leia também</h2>
              <div className="mt-4 grid gap-4 sm:grid-cols-3">
                {others.map((p) => (
                  <Link
                    key={postSlug(p)}
                    to="/blog/$slug"
                    params={{ slug: postSlug(p) }}
                    className="rounded-2xl border border-border bg-card p-4 text-sm font-bold text-brand-deep transition hover:-translate-y-0.5 hover:shadow"
                  >
                    {p.title}
                  </Link>
                ))}
              </div>
            </div>
          ) : null}
        </div>
      </article>
    </>
  );
}
